import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  DailyCommitment,
  MonthlyCommitment,
  getDailyCommitment,
  saveDailyCommitment,
  getMonthlyCommitment,
  saveMonthlyCommitment,
  isMonthlyCommitmentEditable,
  getTodayDate,
  getCurrentMonth,
} from '@/services/commitmentService';

export function useCommitments() {
  const { user } = useAuth();
  const [dailyCommitment, setDailyCommitment] = useState<DailyCommitment | null>(null);
  const [monthlyCommitment, setMonthlyCommitment] = useState<MonthlyCommitment | null>(null);
  const [loading, setLoading] = useState(true);
  const [monthlyEditable, setMonthlyEditable] = useState(false);

  // Load commitments on mount and when user changes
  useEffect(() => {
    if (!user?.id) {
      setLoading(false);
      return;
    }

    const loadCommitments = async () => {
      setLoading(true);
      try {
        const today = getTodayDate();
        const currentMonth = getCurrentMonth();

        // Load daily commitment for today
        const daily = await getDailyCommitment(user.id, today);
        setDailyCommitment(daily);

        // Load monthly commitment for current month
        const monthly = await getMonthlyCommitment(user.id, currentMonth);
        setMonthlyCommitment(monthly);
        
        // Check if monthly is editable
        setMonthlyEditable(isMonthlyCommitmentEditable(monthly));
      } catch (error) {
        console.error('Error loading commitments:', error);
      } finally {
        setLoading(false);
      }
    };

    loadCommitments();
  }, [user?.id]);

  // Save daily commitment
  const saveDailyCommitmentData = useCallback(
    async (commitment: {
      loginFiles: number;
      loginAmount: number;
      disbursementFiles: number;
      disbursementAmount: number;
    }) => {
      if (!user?.id) {
        throw new Error('User must be authenticated');
      }

      const today = getTodayDate();
      const saved = await saveDailyCommitment(user.id, today, commitment);
      setDailyCommitment(saved);
      return saved;
    },
    [user?.id]
  );

  // Save monthly commitment
  const saveMonthlyCommitmentData = useCallback(
    async (commitment: {
      loginFiles: number;
      loginAmount: number;
      disbursementFiles: number;
      disbursementAmount: number;
    }) => {
      if (!user?.id) {
        throw new Error('User must be authenticated');
      }

      const currentMonth = getCurrentMonth();
      const saved = await saveMonthlyCommitment(user.id, currentMonth, commitment);
      setMonthlyCommitment(saved);
      setMonthlyEditable(isMonthlyCommitmentEditable(saved));
      return saved;
    },
    [user?.id]
  );

  return {
    dailyCommitment,
    monthlyCommitment,
    loading,
    monthlyEditable,
    saveDailyCommitment: saveDailyCommitmentData,
    saveMonthlyCommitment: saveMonthlyCommitmentData,
  };
}
